import React, { StrictMode } from 'react';
import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';
import { Auth0Provider } from '@auth0/auth0-react';

import { AUDIENCE, AUTH_DOMAIN, CLIENT_ID } from './constants/AuthConstants';
import { QueryProvider } from './api';
import App from './App';

import './App.css';
import './i18n/init';

// In development, run axe-core after each commit and surface a11y
// violations to the console. The import is gated on NODE_ENV so the
// production bundle never sees axe-core.
if (process.env.NODE_ENV === 'development') {
  import('@axe-core/react').then((axe) => {
    axe.default(React, ReactDOM, 1000);
  });
}

function suppressResizeObserverErrors(): void {
  if (typeof window === 'undefined' || (window as any).__RO_ERROR_PATCHED__) return;
  (window as any).__RO_ERROR_PATCHED__ = true;

  const handler = (event: ErrorEvent | PromiseRejectionEvent) => {
    const message =
      (event as any)?.message ||
      (event as any)?.reason?.message ||
      (typeof (event as any)?.reason === 'string' ? (event as any).reason : '') ||
      '';

    if (
      typeof message === 'string' &&
      (message.includes('ResizeObserver loop completed with undelivered notifications.') ||
        message.includes('ResizeObserver loop limit exceeded'))
    ) {
      event.stopImmediatePropagation?.();
      (event as any).preventDefault?.();
    }
  };

  window.addEventListener('error', handler, { capture: true });
  window.addEventListener('unhandledrejection', handler, { capture: true });
}

// Apply ResizeObserver error suppression once
suppressResizeObserverErrors();

/**
 * Root Application Component
 */
const Root: React.FC = () => (
  <Auth0Provider
    domain={AUTH_DOMAIN}
    clientId={CLIENT_ID}
    redirectUri={`${window.location.origin}/auth`}
    audience={AUDIENCE}
  >
    <QueryProvider>
      <App />
    </QueryProvider>
  </Auth0Provider>
);

/**
 * Mount application
 *
 * React 18 root API. The legacy `ReactDOM.render` still works under
 * React 18 but logs a deprecation; `createRoot` enables concurrent
 * features (automatic batching, transitions, Suspense improvements)
 * and is the path forward to React 19.
 */
const container = document.createElement('div');
document.body.appendChild(container);
const root = createRoot(container);
root.render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
